import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Stack,
  Group,
  Box,
  Text,
  NumberInput,
  Checkbox,
  Button,
  Alert,
  LoadingOverlay,
} from "@mantine/core";
import { IconAlertCircle, IconUsersGroup } from "@tabler/icons-react";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import { useSalonScopedFetch } from "../../hooks/useSalonScopedFetch";
import { Employee, SalonService } from "../../types/salon";
import { StaffTier, StaffTierServiceDefaultInput } from "../../types/staffTier";
import {
  StandardTable,
  StandardTableColumn,
  StandardTableSortDirection,
} from "../common/StandardTable/StandardTable";
import TierSyncModal from "./TierSyncModal";

interface StaffTierServiceDefaultsProps {
  tier: StaffTier;
  canManage: boolean;
  canAssign: boolean;
}

/** The 6 tier-controlled fields, held as editable strings/numbers per service. */
interface ServiceEdit {
  price: number | string;
  variable: boolean;
  base: number | string;
  processing: number | string;
  finishing: number | string;
  buffer: number | string;
}

const numOrNull = (v: number | string): number | null =>
  v === "" || v === null || v === undefined ? null : Number(v);

const editFromSalon = (s: SalonService): ServiceEdit => ({
  price: s.default_price ?? "",
  variable: s.default_is_variable_price ?? false,
  base: s.default_base_duration_mins ?? "",
  processing: s.default_processing_time_mins ?? "",
  finishing: s.default_finishing_time_mins ?? "",
  buffer: s.default_buffer_time_mins ?? "",
});

const DURATION_SEGMENTS: {
  key: "base" | "processing" | "finishing" | "buffer";
  label: string;
  salonField: keyof SalonService;
}[] = [
  { key: "base", label: "Base", salonField: "default_base_duration_mins" },
  {
    key: "processing",
    label: "Proc.",
    salonField: "default_processing_time_mins",
  },
  {
    key: "finishing",
    label: "Finish",
    salonField: "default_finishing_time_mins",
  },
  { key: "buffer", label: "Buffer", salonField: "default_buffer_time_mins" },
];

/**
 * Tier service-defaults editor. Shows every salon service in one table; the
 * rows checked belong to the tier. Checked rows are editable (the 6
 * tier-controlled fields); unchecked rows show the salon service's own defaults,
 * read-only. Saving upserts the checked services and removes the ones that were
 * unchecked, then offers to apply the tier's service list to its members.
 */
const StaffTierServiceDefaults: React.FC<StaffTierServiceDefaultsProps> = ({
  tier,
  canManage,
  canAssign,
}) => {
  const { services, employees, refreshServices } = useSalon() as {
    services: SalonService[] | null;
    employees: Employee[] | null;
    refreshServices: () => Promise<unknown>;
  };
  const {
    getTierServiceDefaults,
    upsertTierServiceDefaults,
    deleteTierServiceDefault,
    applyTierServiceDefaults,
  } = useApp();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, ServiceEdit>>({});
  // "[]" is the serialization of the empty selection, so nothing reads as dirty
  // before the tier's saved defaults have loaded.
  const initialRef = useRef<string>("[]");
  const initialSelectedRef = useRef<Set<string>>(new Set());
  const [sortField, setSortField] = useState<string>("name");
  const [sortDirection, setSortDirection] =
    useState<StandardTableSortDirection>("asc");
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<string | null>(null);

  const members = useMemo(
    () =>
      (employees || [])
        .filter((e) => e.tier_id === tier.id)
        .map((e) => ({
          value: e.id,
          label: `${e.first_name} ${e.last_name}`.trim() || e.email,
        })),
    [employees, tier.id],
  );

  const serialize = (
    sel: Set<string>,
    ed: Record<string, ServiceEdit>,
  ): string =>
    JSON.stringify(
      Array.from(sel)
        .sort()
        .map((id) => [id, ed[id]]),
    );

  const load = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await getTierServiceDefaults(tier.id);
      const byId = new Map(
        (res.service_defaults || []).map((d) => [d.salon_service_id, d]),
      );
      const sel = new Set<string>();
      const ed: Record<string, ServiceEdit> = {};
      for (const s of services || []) {
        const d = byId.get(s.id);
        if (d) {
          sel.add(s.id);
          ed[s.id] = {
            price: d.default_price ?? "",
            variable: d.default_is_variable_price ?? false,
            base: d.default_base_duration_mins ?? "",
            processing: d.default_processing_time_mins ?? "",
            finishing: d.default_finishing_time_mins ?? "",
            buffer: d.default_buffer_time_mins ?? "",
          };
        } else {
          ed[s.id] = editFromSalon(s);
        }
      }
      setSelected(sel);
      setEdits(ed);
      initialSelectedRef.current = new Set(sel);
      initialRef.current = serialize(sel, ed);
    } catch (e) {
      console.error("Error loading tier service defaults:", e);
      setError("Couldn't load the service defaults.");
    } finally {
      setIsLoading(false);
    }
  }, [tier.id, getTierServiceDefaults, services]);

  useSalonScopedFetch(tier.id, load, { tag: "tier-service-defaults" });

  const isDirty = serialize(selected, edits) !== initialRef.current;

  const allIds = useMemo(() => (services || []).map((s) => s.id), [services]);
  const allSelected = allIds.length > 0 && selected.size === allIds.length;

  const sortedServices = useMemo(() => {
    const arr = [...(services || [])];
    arr.sort((a, b) => {
      const av = (
        sortField === "service_type" ? a.service_type || "" : a.name
      ).toLowerCase();
      const bv = (
        sortField === "service_type" ? b.service_type || "" : b.name
      ).toLowerCase();
      const cmp = av.localeCompare(bv);
      return sortDirection === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [services, sortField, sortDirection]);

  const onSort = (key: string): void => {
    if (sortField === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(key);
      setSortDirection("asc");
    }
  };

  const toggleRow = (s: SalonService): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(s.id)) next.delete(s.id);
      else next.add(s.id);
      return next;
    });

  const toggleAll = (): void =>
    setSelected((prev) =>
      prev.size === allIds.length ? new Set() : new Set(allIds),
    );

  const updateEdit = (id: string, patch: Partial<ServiceEdit>): void =>
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const priceCell = (s: SalonService): React.ReactNode => {
    const isSel = selected.has(s.id);
    const e = edits[s.id] ?? editFromSalon(s);
    return (
      <NumberInput
        aria-label={`Price for ${s.name}`}
        value={isSel ? e.price : (s.default_price ?? "")}
        onChange={(v) => updateEdit(s.id, { price: v })}
        min={0}
        prefix="$"
        decimalScale={2}
        hideControls
        size="xs"
        w={100}
        disabled={!canManage || saving || !isSel}
      />
    );
  };

  const variableCell = (s: SalonService): React.ReactNode => {
    const isSel = selected.has(s.id);
    const e = edits[s.id] ?? editFromSalon(s);
    return (
      <Checkbox
        aria-label={`Variable price for ${s.name}`}
        checked={isSel ? e.variable : (s.default_is_variable_price ?? false)}
        onChange={(ev) =>
          updateEdit(s.id, { variable: ev.currentTarget.checked })
        }
        disabled={!canManage || saving || !isSel}
      />
    );
  };

  const durationsCell = (s: SalonService): React.ReactNode => {
    const isSel = selected.has(s.id);
    const e = edits[s.id] ?? editFromSalon(s);
    return (
      <Group gap="xs" wrap="nowrap">
        {DURATION_SEGMENTS.map((seg) => (
          <NumberInput
            key={seg.key}
            label={seg.label}
            aria-label={`${seg.label} minutes for ${s.name}`}
            value={isSel ? e[seg.key] : ((s[seg.salonField] as number) ?? "")}
            onChange={(v) => updateEdit(s.id, { [seg.key]: v })}
            min={0}
            hideControls
            size="xs"
            w={66}
            styles={{
              label: { fontSize: 10, color: "var(--mantine-color-dimmed)" },
            }}
            disabled={!canManage || saving || !isSel}
          />
        ))}
      </Group>
    );
  };

  const columns: StandardTableColumn<SalonService>[] = [
    {
      key: "name",
      label: "Name",
      sortable: true,
      render: (s) => (
        <Text size="sm" fw={500}>
          {s.name}
        </Text>
      ),
    },
    {
      key: "service_type",
      label: "Category",
      sortable: true,
      render: (s) => (
        <Text size="sm" c={s.service_type ? undefined : "dimmed"}>
          {s.service_type || "—"}
        </Text>
      ),
    },
    { key: "price", label: "Price", render: (s) => priceCell(s) },
    {
      key: "variable",
      label: "Is Variable Price",
      align: "center",
      render: (s) => variableCell(s),
    },
    {
      key: "durations",
      label: "Durations (minutes)",
      render: (s) => durationsCell(s),
    },
  ];

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const service_defaults: StaffTierServiceDefaultInput[] = Array.from(
        selected,
      ).map((id) => {
        const e = edits[id];
        return {
          salon_service_id: id,
          default_price: numOrNull(e.price),
          default_is_variable_price: e.variable,
          default_base_duration_mins: numOrNull(e.base),
          default_processing_time_mins: numOrNull(e.processing),
          default_finishing_time_mins: numOrNull(e.finishing),
          default_buffer_time_mins: numOrNull(e.buffer),
        };
      });
      if (service_defaults.length > 0) {
        await upsertTierServiceDefaults(tier.id, { service_defaults });
      }
      // Upsert is additive, so services unchecked since load must be deleted.
      const removed = Array.from(initialSelectedRef.current).filter(
        (id) => !selected.has(id),
      );
      for (const id of removed) {
        await deleteTierServiceDefault(tier.id, id);
      }
      await load();
      // After saving, offer to apply the tier's service list to its members.
      if (canAssign && members.length > 0) {
        setApplyOpen(true);
      }
    } catch (e) {
      console.error("Error saving tier service defaults:", e);
      setError("Couldn't save the service defaults. Check the values.");
    } finally {
      setSaving(false);
    }
  };

  const handleApply = async (staffIds: string[] | "all"): Promise<void> => {
    setApplying(true);
    setApplyResult(null);
    try {
      const res = await applyTierServiceDefaults(tier.id, {
        staff_ids: staffIds,
        acknowledge: true,
      });
      // Applying replaces each member's service menu on the backend; refetch so
      // the in-memory services (with nested per-staff config) aren't stale.
      await refreshServices();
      setApplyResult(res.message);
      setApplyOpen(false);
    } catch (e) {
      console.error("Error applying service defaults:", e);
      setError("Couldn't apply the service defaults to staff.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Stack gap="md" pos="relative" style={{ flex: 1, minHeight: 0 }}>
      <LoadingOverlay visible={isLoading} zIndex={5} />

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {applyResult && (
        <Alert
          color="green"
          withCloseButton
          onClose={() => setApplyResult(null)}
        >
          {applyResult}
        </Alert>
      )}

      <StandardTable<SalonService>
        columns={columns}
        data={sortedServices}
        getRowKey={(s) => s.id}
        loading={saving}
        minWidth={900}
        maxHeight="65dvh"
        stickyHeader
        ariaLabel="Tier service defaults"
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
        selectable
        selectedKeys={selected}
        onToggleRow={(s) => toggleRow(s)}
        onToggleAll={canManage ? toggleAll : undefined}
        isRowSelectable={() => canManage}
        headerSelectionState={{
          checked: allSelected,
          indeterminate: selected.size > 0 && !allSelected,
        }}
        emptyState={
          <Text c="dimmed" size="sm">
            This salon has no services to configure yet.
          </Text>
        }
      />

      <Box
        style={{
          position: "sticky",
          bottom: 0,
          marginTop: "auto",
          paddingTop: "var(--mantine-spacing-md)",
          borderTop: "1px solid var(--mantine-color-gray-3)",
          background: "var(--mantine-color-body)",
        }}
      >
        <Group justify="space-between" align="center">
          <Box>
            {canManage && !isDirty && (
              <Text size="xs" c="dimmed">
                No changes to save yet — check a service, or edit a checked
                service&apos;s defaults.
              </Text>
            )}
          </Box>
          {canManage ? (
            <Button
              color="var(--brand-teal)"
              loading={saving}
              disabled={!isDirty}
              onClick={save}
            >
              Save
            </Button>
          ) : (
            canAssign && (
              <Button
                variant="light"
                color="var(--brand-teal)"
                leftSection={<IconUsersGroup size={16} />}
                onClick={() => setApplyOpen(true)}
                disabled={selected.size === 0 || members.length === 0}
                title={
                  members.length === 0
                    ? "No staff assigned to this tier"
                    : selected.size === 0
                      ? "This tier has no services configured"
                      : undefined
                }
              >
                Apply to members
              </Button>
            )
          )}
        </Group>
      </Box>

      <TierSyncModal
        opened={applyOpen}
        onClose={() => setApplyOpen(false)}
        tierName={tier.name}
        offerServiceReplace
        offerCommission={false}
        members={members}
        defaultChecked
        loading={applying}
        confirmLabel="Apply"
        onConfirm={({ staffIds }) => handleApply(staffIds)}
      />
    </Stack>
  );
};

export default StaffTierServiceDefaults;
